import styled from "styled-components";
import { useTable, useSortBy } from 'react-table'
import React from 'react';

import { teamBelongsToTeamSubset } from './standingsGraphData';


const StyledTd = styled.td`
    text-align: center;
    padding: 0px 8px;
    white-space: nowrap;
`;

const StyledTr = styled.tr`
    text-align: center;
    height: 32px;
`;


const PlayoffGroupTd = styled.td`
    background: ${props => props.backgroundColor};
    color: ${props => props.color};
    writing-mode: sideways-lr;
    height: 2px;
    font-size: 0.8em;
    white-space: nowrap;
    width: 15px;
    position: sticky;
    left: 0px;
    z-index: 1;
    border-right: 1px solid ${props => props.color};
    padding-bottom: 10px;
`;

const StyledTh = styled.th`
    text-align: center;
    background-color: #F4F4F4;
    white-space: nowrap;

`;

const StyledTable = styled.table`
    border-collapse: separate;
    border-spacing: 0px;
    color: #292929;
`;

const StyledTbody = styled.tbody`
`;

const TeamLogo = styled.img`
    width: 32px;
    height: 32px;
    image-rendering: auto; /* Higher-quality downsampling */
`;

const TeamSlug = styled.div`
    position: relative;
    color: ${props => props.$textColour};
    background: ${props => props.$primaryColour};
    display: flex;
    align-items: center;
    text-align: center;
    font-weight: bold;
    height: 18px;
    line-height: 36px;
    border-radius: 5px;
    padding: 0px 10px 2px 4px;
    z-index: 5;

    img {
        z-index: 10;
        posion: absolute;
        vertical-align: middle;
        padding-right: 6px;
    }
`;

const TeamColumnRow = styled.div`
    display: flex;
    white-space: nowrap;
`

const TeamRank = styled.div`
    width: 20px;
    text-align: center;
    padding-right: 4px;
`

const TeamName = styled.div`
    @media (max-width: 768px) {
        display: none;
    }
    font-weight: bold;
    padding-left: 6px;
`

const TeamClinch = styled.div`
    margin-left: auto;
    padding-left: 6px;
`

const StickyTh = styled(StyledTh)`
    ${'' /* box-shadow: 10px 0px 10px 0px rgba(0,0,0,0.25) !important; */}
    ${'' /* border-bottom: 1px solid grey; */}
    position: sticky;
    left: 0px;
    top: 0px;
    background-color: #F4F4F4;
    background: ${props => props.backgroundColor};
    z-index: ${props => props.$zIndex};
    box-shadow: unset;
`;

const StickyTd = styled(StyledTd)`
    -webkit-box-shadow: 10px 0px 10px 0px rgba(0,0,0,0.25);
    -moz-box-shadow: 10px 0px 10px 0px rgba(0,0,0,0.25);
    box-shadow: 10px 0px 10px 0px rgba(0,0,0,0.25);
    position: sticky;
    left: 0px;
    z-index: 1;
    box-shadow: unset;
`;

const tableRowBgColourRGBA = {r: 244, g: 244, b: 244, a: 1.0};  //hsl(0, 0.00%, 95.70%) - page background colour
const oddTableRowBgColourRGBA = {r: 0, g: 0, b: 0, a: 0.06};  // Darker background for odd rows
const championTableRowBgColourRGBA = {r: 208, g: 196, b: 132, a: 1.0};  // #D0C484 - champion row colour
const belowAverageCellColourRGB = {r: 255, g: 46, b: 116}; // #FD2E74 - Colour gradient for below average cells
const aboveAverageCellColourRGB = {r: 8, g: 201, b: 146}; // #08C992 - Colour gradient for above average cells
const gamesBackCellColourRGB = {r: 46, g: 129, b: 255}; // #2E81FD - Colour gradient for games back cells
const playoffGroupColourHex = '#2E81FD'; // #2E81FD
const playInGroupColourHex = '#FD2E74' // #525252
const lotteryGroupColourHex = '#525252' // #FD2E74


function getTableData(data, teamSubset) {
    var tableRows = [];
    for (const dataset of data.datasets) {
        tableRows.push(getTableRow(dataset, teamSubset));
    }
    return tableRows;
}


function calculateColumnValuesMean(values, meanType) {
    if (meanType === 'minimum') {
        return Math.min(...values);
    } else if (meanType === 'maximum') {
        return Math.max(...values);
    } else if (meanType === 'centre') {
        const total = values.reduce((sum, value) => sum + value, 0);
        return total / values.length;
    }
};


function calculateColumnValuesStandardDeviation(values, mean) {
    const variance = values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / values.length;
    return Math.sqrt(variance);
};


function getNumericCellBackgroundColorComponents(value, colMean, colStdDev, columnColourGrade) {
    // Use the column statistics to calculate the Z-score of the cell value, return RGBA colour components based on Z-score
    if (colMean === undefined || colStdDev === undefined) {
        return 'transparent'; // No colouring if no mean or std dev
    }
    const diff = value - colMean;
    const zScore = diff / colStdDev;
    var cellColourRGB;
    if (columnColourGrade.invert) {
        cellColourRGB = zScore >= 0 ? columnColourGrade.negativeColour: columnColourGrade.positiveColour;
    } else {
        cellColourRGB = zScore >= 0 ? columnColourGrade.positiveColour: columnColourGrade.negativeColour;
    }
    var cellAlpha = Math.min(Math.min(Math.abs(zScore), 3) / 3, 1) * 0.5; // Clamp Z-score between -3, +3
    return {...cellColourRGB, a: cellAlpha};
};


function combineRGBAColours(colour1, colour2) {
    // Combine two RGBA colours by blending them using Painter's Algorithm
    const alpha1 = colour1.a;
    const alpha2 = colour2.a;
    const alpha = alpha1 + alpha2 * (1 - alpha1);
    const r = Math.round((colour1.r * alpha1 + colour2.r * alpha2 * (1 - alpha1)) / alpha);
    const g = Math.round((colour1.g * alpha1 + colour2.g * alpha2 * (1 - alpha1)) / alpha);
    const b = Math.round((colour1.b * alpha1 + colour2.b * alpha2 * (1 - alpha1)) / alpha);
    return {r: r, g: g, b: b, a: alpha};
}


function RGBAcolourToString(colour) {
    return `rgba(${colour.r}, ${colour.g}, ${colour.b}, ${colour.a})`;
}


function extractKeyFromProps(props) {
    const { key, ...restProps } = props;
    return { key, restProps };
}


function parseRecordStringAsPct(recordString) {
    // Parses a record-like string (W-L) into a win percentage
    if (recordString == null) {
        return 0;
    }
    let record = recordString.split('-');
    return parseInt(record[0]) / (parseInt(record[0]) + parseInt(record[1]));
}


function parseStreakString(streakString) {
    // Parses a streak string (W3, L2) into a win/loss streak
    let streakDirection = streakString.includes('W') ? 1 : -1;
    let streakInt = parseInt(streakString.match(/\d+/)[0]) * streakDirection;
    return streakInt;
}


function parsePointDiffString(pointDiffString) {
    // Parses a point differential string (+3.4, -2.1) into a float
    return parseFloat(pointDiffString);
}


function sortRecordColumnRows(rowA, rowB, columnId, desc) {
    // Sorts rows based on the win percentage of record-like (W-L) value of the cell
    const cellA = rowA.values[columnId];
    const cellB = rowB.values[columnId];
    let winPctA = parseRecordStringAsPct(cellA);
    let winPctB = parseRecordStringAsPct(cellB);
    return winPctA > winPctB ? 1 : -1;
}


function sortTeamColumnRows(rowA, rowB, columnId, desc, teamSubset) {
    // Sorts rows based on the team rank/seed
    const rankA = teamSubset.selected == 'all' ? rowA.original.teamObj.league_rank : rowA.original.teamObj.conference_seed;
    const rankB = teamSubset.selected == 'all' ? rowB.original.teamObj.league_rank : rowB.original.teamObj.conference_seed;
    return rankA > rankB ? 1 : -1;
}


function sortStreakColumnRows(rowA, rowB, columnId, desc) {
    // Sorts rows based on the win/loss streak of the cell
    const streakStringA = rowA.values[columnId];
    const streakStringB = rowB.values[columnId];
    let streakIntA = parseStreakString(streakStringA);
    let streakIntB = parseStreakString(streakStringB);
    return streakIntA > streakIntB ? 1 : -1;
}


function sortPointDiffColumnRows(rowA, rowB, columnId, desc) {
    // Sorts rows based on the point differential of the cell
    const pointDiffStringA = rowA.values[columnId];
    const pointDiffStringB = rowB.values[columnId];
    let pointDiffA = parsePointDiffString(pointDiffStringA);
    let pointDiffB = parsePointDiffString(pointDiffStringB);
    return pointDiffA > pointDiffB ? 1 : -1;
}


function getTableRow(dataset, teamSubset) {
    const standingsInfo = dataset.team.standings_info;
    let clinchString = '';
    let clinchTitle = '';
    if (dataset.champions) {
        clinchString = '🏆';
        clinchTitle = `Champions (vs. ${dataset.championshipOppTeam})`;
    } else if (standingsInfo.clinched_playoffs == 1) {
        clinchString = '✔️';
        clinchTitle = 'Clinched Playoffs';
    } else if (standingsInfo.clinched_playin == 1) {
        clinchString = '🆚';
        clinchTitle = 'Clinched Play-In';
    } else if (standingsInfo.eliminated == 1) {
        clinchString = '❌';
        clinchTitle = 'Eliminated';
    }

    const streak = standingsInfo.streak;
    let prefix = ((streak > 0) ? 'W' : 'L');
    let streakEmoji = '';

    if (streak >= 3) {
        streakEmoji = '🔥';
    } else if (streak <= -3) {
        streakEmoji = '❄️';
    }
    let absStreak = Math.abs(streak);
    let streakEmojis = streakEmoji.repeat(Math.min(3, Math.floor(absStreak/3)));  // Cap at 3, shoutout 2023 Pistons
    let streakString = `${prefix}${Math.abs(absStreak)} ${streakEmojis}`;

    let team =
        <TeamColumnRow>
            <TeamRank>{teamSubset.selected == 'all' ? dataset.team.league_rank : dataset.team.conference_seed}</TeamRank>
            <TeamSlug $primaryColour={dataset.primaryColour} $textColour={dataset.textColour}>
                <TeamLogo src={dataset.logoURL}/>
                {dataset.team.slug}
            </TeamSlug>
            <TeamName>{dataset.team.name}</TeamName>
            <TeamClinch title={clinchTitle}>{clinchString}</TeamClinch>
        </TeamColumnRow>
        ;

    return {
        teamObj: dataset.team,
        team: team,
        wins: standingsInfo.wins,
        losses: standingsInfo.losses,
        winPct: standingsInfo.wins / (standingsInfo.wins + standingsInfo.losses),
        gamesBack: teamSubset.selected == 'all' ? standingsInfo.league_games_back : standingsInfo.conference_games_back,
        home: standingsInfo.home,
        road: standingsInfo.road,
        pointsPerGame: standingsInfo.points_per_game,
        opponentPointsPerGame: standingsInfo.opponent_points_per_game,
        pointDifferential: standingsInfo.point_differential,
        last10: standingsInfo.last_10,
        streak: streakString.trim(),
        champions: dataset.champions,
    };
}

function getColumns(tableData, columnSortFnsById) {
    var columns = [
        // {
        //     id: 'playoffGroup',
        //     Header: 'b',
        //     hoverText: '',
        //     accessor: () => '',
        //     disableSortBy: true,
        //     numericParserFn: (value) => value,
        //     colourGrade: null,
        // },
        {
            id: 'team',
            Header: 'Team',
            hoverText: '',
            accessor: row => row.team,
            numericParserFn: (value) => value,
            colourGrade: null,
        },
        {
            id: 'wins',
            Header: 'W',
            hoverText: 'Total wins',
            accessor: row => row.wins,
            numericParserFn: (value) => value,
            colourGrade: null,
        },
        {
            id: 'losses',
            Header: 'L',
            hoverText: 'Total losses',
            accessor: row => row.losses,
            numericParserFn: (value) => value,
            colourGrade: null,
        },
        {
            id: 'winPct',
            Header: 'Win %',
            hoverText: 'Win percentage',
            accessor: row => row.winPct.toFixed(3),
            numericParserFn: (value) => value,
            colourGrade: {mean: 'centre', invert: false, negativeColour: belowAverageCellColourRGB, positiveColour: aboveAverageCellColourRGB},
        },
        {
            id: 'gamesBack',
            Header: 'GB',
            hoverText: 'Games back',
            accessor: row => (row.gamesBack || '-'),
            numericParserFn: (value) => value == '-' ? 0 : value,
            colourGrade: {mean: 'minimum', invert: true, negativeColour: belowAverageCellColourRGB, positiveColour: aboveAverageCellColourRGB},

        },
        {
            id: 'home',
            Header: 'Home',
            hoverText: 'Home record',
            accessor: row => row.home,
            numericParserFn: parseRecordStringAsPct,
            colourGrade: {mean: 'centre', invert: false, negativeColour: belowAverageCellColourRGB, positiveColour: aboveAverageCellColourRGB},
        },
        {
            id: 'road',
            Header: 'Road',
            hoverText: 'Road record',
            accessor: row => row.road,
            numericParserFn: parseRecordStringAsPct,
            colourGrade: {mean: 'centre', invert: false, negativeColour: belowAverageCellColourRGB, positiveColour: aboveAverageCellColourRGB},
        },
        {
            id: 'pointsPerGame',
            Header: 'PPG',
            hoverText: 'Points per game',
            accessor: row => row.pointsPerGame.toFixed(1),
            numericParserFn: (value) => value,
            colourGrade: {mean: 'centre', invert: false, negativeColour: belowAverageCellColourRGB, positiveColour: aboveAverageCellColourRGB},
        },
        {
            id: 'opponentPointsPerGame',
            Header: 'Opp. PPG',
            hoverText: 'Opponent points per game',
            accessor: row => row.opponentPointsPerGame.toFixed(1),
            invertGradient: true,
            numericParserFn: (value) => value,
            colourGrade: {mean: 'centre', invert: true, negativeColour: belowAverageCellColourRGB, positiveColour: aboveAverageCellColourRGB},
        },
        {
            id: 'pointDifferential',
            Header: 'Diff.',
            hoverText: 'Point differential',
            accessor: row => (row.pointDifferential >= 0 ? `+${row.pointDifferential.toFixed(1)}` : row.pointDifferential.toFixed(1)),
            numericParserFn: parsePointDiffString,
            colourGrade: {mean: 'centre', invert: false, negativeColour: belowAverageCellColourRGB, positiveColour: aboveAverageCellColourRGB},
        },
        {
            id: 'last10',
            Header: 'Last 10',
            hoverText: 'Last 10 games record',
            accessor: row => row.last10,
            numericParserFn: parseRecordStringAsPct,
            colourGrade: {mean: 'centre', invert: false, negativeColour: belowAverageCellColourRGB, positiveColour: aboveAverageCellColourRGB},
        },
        {
            id: 'streak',
            Header: 'Streak',
            hoverText: 'Win/loss streak',
            accessor: row => row.streak,
            numericParserFn: parseStreakString,
            colourGrade: null,
        },
    ];

    columns.forEach(column => {
        column.sortType = columnSortFnsById[column.id] || 'alphanumeric';
    });

    // Calculate global statistics for each column (for use in cell colouring)
    if (tableData.length > 0) {
        columns.forEach(column => {
            if (column.colourGrade == null) {
                return;
            }
            const values = tableData.map(row => column.numericParserFn(row[column.id]));
            const mean = calculateColumnValuesMean(values, column.colourGrade.mean);
            const stdDev = calculateColumnValuesStandardDeviation(values, mean);
            column.colourGrade.stats = { 'mean': mean, 'stdDev': stdDev };
        });
    };

    return columns;
}



function StandingsTable({data, teamSubset, numPlayoffTeams, numPlayInTeams}) {
    const teamSortFn = React.useMemo(() => (rowA, rowB, columnId, desc) => sortTeamColumnRows(rowA, rowB, columnId, desc, teamSubset), [teamSubset]);
    const recordSortFn = React.useMemo(() => (rowA, rowB, columnId, desc) => sortRecordColumnRows(rowA, rowB, columnId, desc), []);
    const streakSortFn = React.useMemo(() => (rowA, rowB, columnId, desc) => sortStreakColumnRows(rowA, rowB, columnId, desc), []);
    const pointDiffSortFn = React.useMemo(() => (rowA, rowB, columnId, desc) => sortPointDiffColumnRows(rowA, rowB, columnId, desc), []);

    const columnSortFnsById = React.useMemo(() => ({
        'team': teamSortFn,
        'home': recordSortFn,
        'road': recordSortFn,
        'last10': recordSortFn,
        'streak': streakSortFn,
        'pointDifferential': pointDiffSortFn,
    }), [teamSortFn, recordSortFn, streakSortFn, pointDiffSortFn]);

    const tableData = React.useMemo(() => getTableData(data, teamSubset), [data, teamSubset]);
    const columns = React.useMemo(() => getColumns(tableData, columnSortFnsById), [tableData, columnSortFnsById]);
    const defaultSort = React.useMemo(() => [{id: 'team', desc: false}], []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state: { sortBy },
    } = useTable({ columns, data: tableData, initialState: {sortBy: defaultSort}, disableSortRemove: true }, useSortBy)


    // Filter rows based on teamSubset
    const filteredRows = rows.filter(row => { return teamBelongsToTeamSubset(row.original.teamObj, teamSubset.selected) });

    const isTeamColumnSortedAsc = sortBy.length > 0 && sortBy[0].id === 'team' && !sortBy[0].desc;
    const groupDelimetersByIdx = new Map();
    if ((teamSubset.selected == 'East' || teamSubset.selected == 'West') && isTeamColumnSortedAsc) {
        groupDelimetersByIdx.set(0, {text: 'Playoffs', colour: playoffGroupColourHex, span: numPlayoffTeams});
        if (numPlayInTeams > 0) {
            groupDelimetersByIdx.set(numPlayoffTeams, {text: 'Play-in', colour: playInGroupColourHex, span: numPlayInTeams});
        }
        groupDelimetersByIdx.set(numPlayoffTeams + numPlayInTeams, {text: 'Lottery', colour: lotteryGroupColourHex, span: filteredRows.length - (numPlayoffTeams + numPlayInTeams)});
    }
    return (
        <StyledTable {...getTableProps()}>
            <thead>
                {headerGroups.map(headerGroup => {
                    const { key, restProps } = extractKeyFromProps(headerGroup.getHeaderGroupProps());
                    return (
                    <StyledTr key={key} {...restProps}>
                        {headerGroup.headers.map((column, index) => {
                            const { key, restProps } = extractKeyFromProps(column.getHeaderProps(column.getSortByToggleProps()));
                            return (
                                <StickyTh key={key} {...restProps} $zIndex={index === 0 ? 3 : 2} title={column.hoverText}>
                                    {column.render('Header')}
                                    <span>
                                        {column.isSorted ? column.isSortedDesc ? '🔽' : '🔼' : ''}
                                    </span>
                                </StickyTh>
                                );
                            })}
                    </StyledTr>
                )})}
            </thead>
            <StyledTbody {...getTableBodyProps()}>
                {filteredRows.map((row, rowIdx) => {
                    prepareRow(row)
                    const isChampion = row.original.champions;
                    const { key, restProps } = extractKeyFromProps(row.getRowProps());

                    return (
                        <StyledTr key={key} {...restProps}>
                            {row.cells.map((cell, colIdx) => {
                                const TdComponent = colIdx === 0 ? StickyTd : StyledTd;

                                // Get the base row colour based on index
                                let backgroundColorRGBA = tableRowBgColourRGBA;
                                if (rowIdx % 2 === 0) {
                                    backgroundColorRGBA = combineRGBAColours(oddTableRowBgColourRGBA, backgroundColorRGBA);
                                }
                                if (cell.column.colourGrade != null) {
                                    // Add statistical colouring to the base row colour
                                    const stats = cell.column.colourGrade.stats;
                                    let value = cell.column.numericParserFn(cell.value);
                                    let staisticalRGBA = getNumericCellBackgroundColorComponents(value, stats.mean, stats.stdDev, cell.column.colourGrade);
                                    backgroundColorRGBA = combineRGBAColours(staisticalRGBA, backgroundColorRGBA);
                                } else if (isChampion) {
                                    // Highlight champion row
                                    backgroundColorRGBA = championTableRowBgColourRGBA;
                                }

                                let backgroundColor = RGBAcolourToString(backgroundColorRGBA);
                                const { key, restProps } = extractKeyFromProps(cell.getCellProps());
                                return (
                                    <TdComponent key={key} {...restProps} style={{ backgroundColor }}>
                                        {cell.render('Cell')}
                                    </TdComponent>
                                );
                            })}
                        </StyledTr>
                    );
                })}
            </StyledTbody>
        </StyledTable>
    )
}


export { StandingsTable, TeamSlug };
